/**
 * Map label grouping. Price labels on neighbouring listings collide on screen;
 * this finds which labels overlap at a given (possibly fractional) zoom so the
 * map can merge each overlapping set into one group label, and says whether a
 * group is effectively one address (`tight`). A group label is usually larger
 * than one price label (a stacked list), so `groupStackedLabels` regroups at the
 * rendered sizes until no group label collides. Pure Web Mercator maths: no map
 * instance, no DOM, deterministic for a given input order.
 */

/** Label size is in CSS pixels; the label is anchored bottom-centre on the point (AdvancedMarkerElement default). */
export interface LabelPoint { id: string; lat: number; lng: number; width: number; height: number }
/**
 * `ids` keep input order. `lat`/`lng` is the arithmetic mean of the members: a
 * label anchor for a small on-screen cluster, not a geodesic centroid.
 * `spanMetres` is the largest pairwise distance (0 for a singleton).
 */
export interface LabelGroup { ids: string[]; lat: number; lng: number; tight: boolean; spanMetres: number }
/** `gap`: px of breathing room (labels closer than this still overlap), default 4; `tightMetres`: default 30. */
export interface GroupOptions { gap?: number; tightMetres?: number }
/**
 * `renderedSize`: CSS pixel size of the group label drawn for `members` (input
 * order), anchored like a price label; singletons keep their own size.
 * `passes`: maximum regroup rounds after the plain overlap grouping, default 3.
 */
export interface StackOptions extends GroupOptions {
  renderedSize: (members: LabelPoint[], tight: boolean) => { width: number; height: number }
  passes?: number
}

const TILE_PIXELS = 256
const MAX_LATITUDE = 85.05113
const EARTH_RADIUS_METRES = 6371008.8
const RADIANS = Math.PI / 180

interface LabelBox { index: number; left: number; right: number; top: number; bottom: number }

/** Web Mercator world pixels for 256px tiles at `zoom`; latitude clamps to the Mercator limit. */
export function projectToWorldPixels(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const scale = TILE_PIXELS * 2 ** zoom
  const sin = Math.sin(Math.min(Math.max(lat, -MAX_LATITUDE), MAX_LATITUDE) * RADIANS)
  return { x: (lng + 180) / 360 * scale, y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale }
}

/** Great-circle (haversine) distance in metres on the mean Earth radius. */
export function metresBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = (b.lat - a.lat) * RADIANS
  const dLng = (b.lng - a.lng) * RADIANS
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * RADIANS) * Math.cos(b.lat * RADIANS) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Non-finite or negative label sizes count as 0 so odd input never throws. */
function size(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0
}

function find(parent: number[], index: number): number {
  let root = index
  for (let next = parent[root]; next !== undefined && next !== root; next = parent[root]) root = next
  for (let node = index; node !== root;) {
    const next = parent[node] ?? root
    parent[node] = root
    node = next
  }
  return root
}

/** Union keeps the smaller input index as root, so a component's root is its first member. */
function union(parent: number[], a: number, b: number): void {
  const rootA = find(parent, a)
  const rootB = find(parent, b)
  if (rootA !== rootB) parent[Math.max(rootA, rootB)] = Math.min(rootA, rootB)
}

/**
 * Connected components of the on-screen overlap graph for `gap` px of breathing
 * room. Components are ordered by their first member's input index and keep
 * members in input order; generic so callers can carry bookkeeping on points.
 */
function overlapComponents<T extends LabelPoint>(points: T[], zoom: number, gap: number): T[][] {
  const pad = Number.isFinite(gap) ? gap / 2 : 0
  const parent = points.map((_, index) => index)
  const boxes: LabelBox[] = []
  points.forEach((point, index) => {
    const { x, y } = projectToWorldPixels(point.lat, point.lng, zoom)
    const halfWidth = size(point.width) / 2
    const box = { index, left: x - halfWidth - pad, right: x + halfWidth + pad, top: y - size(point.height) - pad, bottom: y + pad }
    // A label with no finite position cannot overlap anything; it stays a singleton.
    if (Number.isFinite(box.left) && Number.isFinite(box.right) && Number.isFinite(box.top) && Number.isFinite(box.bottom)) boxes.push(box)
  })
  boxes.sort((a, b) => a.left - b.left || a.index - b.index)

  const active: LabelBox[] = []
  for (const box of boxes) {
    let kept = 0
    for (const other of active) {
      // Later boxes start at or right of this one, so a box ending here is finished.
      if (other.right <= box.left) continue
      active[kept++] = other
      if (other.left < box.right && other.top < box.bottom && other.bottom > box.top) union(parent, other.index, box.index)
    }
    active.length = kept
    active.push(box)
  }

  const components = new Map<number, T[]>()
  points.forEach((point, index) => {
    const root = find(parent, index)
    const component = components.get(root)
    if (component) component.push(point)
    else components.set(root, [point])
  })
  return [...components.values()]
}

/** Group summary over all `members` (non-empty, input order). */
function summarise(members: LabelPoint[], tightMetres: number): LabelGroup {
  let spanMetres = 0
  members.forEach((a, i) => members.forEach((b, j) => { if (j > i) spanMetres = Math.max(spanMetres, metresBetween(a, b)) }))
  return {
    ids: members.map(point => point.id),
    lat: members.reduce((sum, point) => sum + point.lat, 0) / members.length,
    lng: members.reduce((sum, point) => sum + point.lng, 0) / members.length,
    tight: spanMetres <= tightMetres,
    spanMetres,
  }
}

/**
 * Connected components of the on-screen overlap graph (transitive: A-B and B-C
 * put A, B and C together). Groups are ordered by their first member's input
 * index and singletons are groups of one. A sort-by-left sweep keeps this
 * O(n log n + overlaps) for the typical sparse case.
 */
export function groupOverlappingLabels(points: LabelPoint[], zoom: number, options: GroupOptions = {}): LabelGroup[] {
  const tightMetres = options.tightMetres ?? 30
  return overlapComponents(points, zoom, options.gap ?? 4).map(members => summarise(members, tightMetres))
}

interface StackMember { index: number; point: LabelPoint }
interface Stack { members: StackMember[]; group: LabelGroup }

/**
 * Overlap groups at their rendered size: start from `groupOverlappingLabels`,
 * then regroup one stand-in label per group (its own size for a singleton,
 * `renderedSize` otherwise) at the group anchor, merging groups whose labels
 * collide, until nothing merges or `passes` runs out. A merged group is
 * summarised over all of its members (mean anchor, span, `tight`), so its anchor
 * is not a mean of group anchors. Same ordering as `groupOverlappingLabels`.
 */
export function groupStackedLabels(points: LabelPoint[], zoom: number, options: StackOptions): LabelGroup[] {
  const gap = options.gap ?? 4
  const tightMetres = options.tightMetres ?? 30
  const passes = options.passes ?? 3
  const stackOf = (members: StackMember[]): Stack => ({ members, group: summarise(members.map(member => member.point), tightMetres) })
  // Members carry their input index: ids may repeat, and merges re-sort by it.
  let stacks = overlapComponents(points.map((point, index) => ({ ...point, member: { index, point } })), zoom, gap)
    .map(component => stackOf(component.map(entry => entry.member)))

  for (let pass = 0; pass < passes; pass++) {
    const standIns = stacks.map(stack => {
      const [only] = stack.members
      const rendered = only && stack.members.length === 1 ? only.point : options.renderedSize(stack.members.map(member => member.point), stack.group.tight)
      return { id: '', lat: stack.group.lat, lng: stack.group.lng, width: rendered.width, height: rendered.height, stack }
    })
    const merged = overlapComponents(standIns, zoom, gap)
    // Components partition the stand-ins, so equal counts mean every result is a singleton.
    if (merged.length === stacks.length) break
    stacks = merged.map(component => {
      const [only] = component
      return only && component.length === 1
        ? only.stack
        : stackOf(component.flatMap(standIn => standIn.stack.members).sort((a, b) => a.index - b.index))
    })
  }
  return stacks.map(stack => stack.group)
}
